import React, { useState } from 'react';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword, multiFactor, TotpMultiFactorGenerator, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, collection, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import logo from '../images/unnamed.png';
import QRCode from 'qrcode.react';

function AuthFormNew() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loanPurpose, setLoanPurpose] = useState('Acquisition');
  const [loading, setLoading] = useState(false);
  const [enrollTOTP, setEnrollTOTP] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loanApplicationId, setLoanApplicationId] = useState(null);
  const navigate = useNavigate();

  const sendSignUpNotification = async (userDetails) => {
    try {
      const emailRef = collection(db, 'email');
      const emailData = {
        to: "max@sbacapital.ai",
        from: "Isak @ Lended <isak@lended.ai>",
        replyTo: "isak@lended.ai",
        subject: `New Sign Up - ${loanPurpose}`,
        templateName: "SignUpNotification",
        message: {
          subject: `New Sign Up - ${loanPurpose}`,
          html: `
            <p>Hello Max,</p>
            <p>A new user has signed up for ${loanPurpose}:</p>
            <ul>
              <li>Name: ${userDetails.firstName} ${userDetails.lastName}</li>
              <li>Email: ${userDetails.email}</li>
              <li>Phone Number: ${userDetails.phoneNumber}</li>
            </ul>
            <p>Best regards,<br>Lended AI System</p>
          `,
        },
      };

      await addDoc(emailRef, emailData);
      console.log('Sign-up notification email sent successfully');
    } catch (error) {
      console.error('Error sending sign-up notification email:', error);
    }
  };

  const handleEmailSignUp = async () => {
    if (!termsAccepted) {
      alert('You must agree to the terms of service.');
      return;
    }
    if (email && password && firstName && lastName && phoneNumber) {
      try {
        setLoading(true);

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const userProfileRef = doc(db, 'users', user.uid);
        await setDoc(userProfileRef, {
          email: user.email,
          firstName,
          lastName,
          phoneNumber,
          status: 'Pre-lender',
          lastUserUpdate: new Date(),
        });

        const lenderMatchRef = await addDoc(collection(db, 'lenderMatches'), {
          firstName,
          lastName,
          email,
          phoneNumber,
          businessName: '',
          businessWebsite: '',
          businessDescription: '',
          industry: '',
          experience: '',
          requestedAmount: '',
          useOfFunds: '',
          useOfFundsDescription: '',
          businessPlan: 'None',
          financialProjections: false,
          generatingRevenue: 'None',
          revenue: '',
          status: 'pending',
          createdAt: new Date()
        });

        const loanApplicationRef = await addDoc(collection(db, 'loanApplications'), {
          action: `call ${firstName} to see what they are working on (new signup)`,
          called: false,
          allDocumentsUploaded: false,
          userId: user.uid,
          firstName,
          lastName,
          email,
          phoneNumber,
          businessName: '',
          businessAge: '',
          businessState: '',
          loanSize: '',
          industry: '',
          capTable: [],
          ebitda: '',
          revenue: '',
          additionalNotes: '',
          status: 'Pre-lender',
          createdAt: new Date(),
          updatedAt: new Date(),
          personalGuarantors: [{ firstName, lastName }],
          realEstateAppraisal: '0',
          lastUserUpdate: new Date(),
          loanPurpose: loanPurpose,
          fromLenderMatch: true,
          lenderMatchId: lenderMatchRef.id
        });

        setLoanApplicationId(loanApplicationRef.id);

        const userApplicationsRef = doc(db, 'users', user.uid, 'applications', loanApplicationRef.id);
        await setDoc(userApplicationsRef, {
          applicationID: loanApplicationRef.id,
          status: 'Pre-lender',
        });

        await sendSignUpNotification({
          firstName,
          lastName,
          email,
          phoneNumber
        });

        if (enrollTOTP) {
          await sendEmailVerification(user);
          alert('A verification email has been sent to your email address. Please verify your email to complete the sign-up process.');
          setEmailSent(true);
        } else {
          setTimeout(() => {
            setLoading(false);
            if (loanPurpose === 'workingCapital') {
              navigate(`/shared-upload/${loanApplicationRef.id}`);
            } else {
              navigate('/portal', { state: { retryCount: 0 } });
            }
          }, 2000);
        }

        setLoading(false);
      } catch (error) {
        console.log('Error signing up:', error);
        setLoading(false);
        if (error.code === 'auth/email-already-in-use') {
          alert('The email address is already in use. Please use a different email.');
        } else {
          alert('Error signing up. Please try again.');
        }
      }
    } else {
      alert('Please enter all required fields.');
    }
  };

  const checkEmailVerification = async () => {
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        console.log('Email verified');
        setEmailVerified(true);
        const multiFactorSession = await multiFactor(auth.currentUser).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
        setTotpSecret(totpSecret);
      } else {
        alert('Please verify your email before proceeding.');
      }
    } catch (error) {
      console.log('Error checking email verification:', error);
      alert('Error verifying email. Please try again.');
    }
  };

  const handleTOTPEnrollment = async () => {
    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
      );
      await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'TOTP');
      console.log('TOTP enrollment successful');

      setTimeout(() => {
        setLoading(false);
        if (loanPurpose === 'workingCapital') {
          navigate(`/shared-upload/${loanApplicationId}`);
        } else {
          navigate('/portal', { state: { retryCount: 0 } });
        }
      }, 2000);
    } catch (error) {
      console.log('Error enrolling TOTP:', error);
      alert('Invalid verification code. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-blue-900 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-white p-10 rounded-xl shadow-2xl">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src={logo}
            alt="Your Company"
          />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create an account to get a quick pre-check on your financials
          </p>
        </div>

        {!emailSent ? (
          <div className="space-y-6">
            <div className="rounded-md shadow-sm space-y-4">
              <div className="flex space-x-4">
                <div className="w-1/2 relative">
                  <label htmlFor="firstName" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="John"
                  />
                </div>
                <div className="w-1/2 relative">
                  <label htmlFor="lastName" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="relative">
                <label htmlFor="phoneNumber" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="(123) 456-7890"
                />
              </div>

              <div className="relative">
                <label htmlFor="loanPurpose" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                  Loan Purpose
                </label>
                <select
                  id="loanPurpose"
                  value={loanPurpose}
                  onChange={(e) => setLoanPurpose(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                >
                  <option value="Acquisition">Business Acquisition</option>
                  <option value="workingCapital">Working Capital</option>
                </select>
              </div>

              <div className="relative">
                <label htmlFor="email" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="you@example.com"
                />
              </div>

              <div className="relative">
                <label htmlFor="password" className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-900">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center">
                <input
                  id="enrollTOTP"
                  name="enrollTOTP"
                  type="checkbox"
                  checked={enrollTOTP}
                  onChange={(e) => setEnrollTOTP(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enrollTOTP" className="ml-2 block text-sm text-gray-900">
                  Enable Two-Factor Authentication
                </label>
              </div>

              <div className="flex items-center">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-900">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    terms of service
                  </button>
                </label>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={handleEmailSignUp}
                disabled={loading}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </div>
        ) : emailVerified ? (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Set up Two-Factor Authentication</h3>
            <div className="mt-4 flex justify-center">
              {totpSecret && <QRCode value={totpSecret.generateQrCodeUrl(email, 'Lended')} />}
            </div>
            <div className="mt-4">
              <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <button
              type="button"
              onClick={handleTOTPEnrollment}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Verify and Complete Setup
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <p className="text-center text-sm text-gray-600">
              Please check your email and verify your account. Once verified, click the button below.
            </p>
            <button
              type="button"
              onClick={checkEmailVerification}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              I've verified my email
            </button>
          </div>
        )}

        {showTerms && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Terms of Service</h3>
              <div className="prose prose-sm text-gray-500">
                <p>By using our services, you agree to:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Allow us to receive referral compensation for matching you with a bank</li>
                  <li>Share your information with potential lenders</li>
                  <li>Maintain the confidentiality of your account</li>
                </ul>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowTerms(false)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuthFormNew;